<template lang="pug">
  v-form.dynamic-field(ref="form-input" lazy-validation :class="fieldClass" @submit.prevent="() => {}")
    v-combobox(
      v-if="field.is_vv_nr"
      v-model="fieldValue"
      outlined
      :id="`dynamic-field-${ field.id }`"
      :label="field.name"
      :items="vertragNumbers"
      :readonly="!!field.is_calculated"
      :rules="[ field.mandatory ? rules.required : true ]"
      @change="(e) => onChangeValue(e)"
      @focus="loadVertragNumbers"
      @update:search-input="onSearchValue"
    )

    v-text-field(
      v-else-if="field.type === 's' || field.type === 'r' || field.type === 'c'"
      v-model="fieldValue"
      outlined
      :id="`dynamic-field-${ field.id }`"
      :label="field.name"
      :readonly="!!field.is_calculated"
      :rules="[ field.mandatory ? rules.required : true ]"
      @change="(e) => onChangeValue(e)"
    )

    n-number-input(
      v-else-if="field.type === 'i' || field.type === 'f' || field.type === 'w' || field.type === 'W'"
      :id="`dynamic-field-${ field.id }`"
      :value="fieldValue"
      :label="field.name"
      :type="field.type"
      :decimals="fieldDecimals"
      :hide-thousand-separator="field.styling && field.styling.thousands_separator === false"
      :readonly="!!field.is_calculated"
      :input-rules="[ field.mandatory ? rules.required : true ]"
      @change="(e) => onChangeValue(e)"
    )

    v-textarea(
      v-else-if="field.type === 'm'"
      v-model="fieldValue"
      outlined
      :id="`dynamic-field-${ field.id }`"
      :label="field.name"
      :readonly="!!field.is_calculated"
      :row="5"
      :rules="[ field.mandatory ? rules.required : true ]"
      @change="(e) => onChangeValue(e)"
    )

    v-select(
      v-else-if="field.type === 'd'"
      v-model="fieldValue"
      dense
      outlined
      :id="`dynamic-field-${ field.id }`"
      :label="field.name"
      :items="field.v_dropdown || []"
      :readonly="!!field.is_calculated"
      :rules="[ field.mandatory ? rules.required : true ]"
      @change="(e) => onChangeValue(e)"
    )

    n-date-picker(
      v-else-if="field.type === 't'"
      v-model="fieldValue"
      :id="`dynamic-field-${ field.id }`"
      :label="field.name"
      :format="`YYYY-MM-DD[T]HH:mm:ss.SSS[Z]`"
      :display-format="getDisplayFormat(field.v_time_format)"
      :readonly="!!field.is_calculated"
      :input-rules="[ field.mandatory ? rules.required : true, rules.valid_date(field.v_time_format) ]"
      :type="getPickerType(field.v_time_format)"
      @change="(e) => onChangeValue(e)"
    )

    n-radio(
      v-else-if="field.type === 'b'"
      v-model="fieldValue"
      :id="`dynamic-field-${ field.id }`"
      :readonly="!!field.is_calculated"
      @change="(e) => onChangeValue(e)"
    )

    a.mb-2(v-if="field.show_calculator && !field.is_calculated" @click="onShowCalcModal")
      v-icon(color="primary") mdi-calculator

    v-dialog(v-if="field.show_calculator && !field.is_calculated" v-model="showCalcModal" width="420")
      .white.pa-5
        n-calculator(v-model="calcValue" ref="calculator")

        v-row.mt-3
          v-col(cols="4"): v-btn(
            color="secondary"
            large
            outlined
            @click="showCalcModal = false"
          )
            span.primary--text schließen

          v-col.text-right(cols="8"): v-btn(
            color="primary"
            large
            :disabled="calcValue === null"
            @click="onApplyCalcValue"
          ) Ergebnis Übernehmen

</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator'

import FormMixin from '@/mixins/form'
import Urls from '@/router/urls'

@Component({
  mixins: [
    FormMixin
  ],

  components: {
    NCalculator: () => import('@/components/common/n-calculator'),
    NNumberInput: () => import('@/components/common/n-number-input'),
  },

  watch: {
    field () {
      this.fieldValue = this.field.value
    }
  }
})
export default class DynamicField extends Vue {
  @Prop({ type: Object, default: () => {} }) field

  calcValue = null
  fieldValue = null
  showCalcModal = false
  searchTimer = null
  vertragNumbers = []

  get fieldClass () {
    if (!this.showEmptyNotice) {
      return ''
    }

    return this.isEmptyField(this.field)
      ? 'empty-highlight'
      : ''
  }

  get fieldDecimals () {
    return this.field.type === 'w' || this.field.type === 'f'
      ? 2
      : 0
  }

  mounted () {
    this.isFormValid('form-input')
    this.searchTimer = null
    this.fieldValue = this.field.value
    // if (this.field.is_vv_nr) {
    //   this.loadVertragNumbers()
    // }
  }

  beforeDestroy () {
    this.searchTimer = null
  }

  getDisplayFormat (format) {
    return format === 'YYYY'
      ? 'YYYY'
      : 'DD.MM.YYYY'
  }

  getPickerType (format) {
    return format === 'YYYY'
      ? 'year'
      : 'date'
  }

  loadVertragNumbers () {
    if (!this.$route.params || !this.$route.params.id) {
      return
    }

    const consultationId = this.$route.params.id
    const params = {
      current_consultation_field_id: this.field.id,
      for_konzept: this.isConcept
    }

    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
      this.searchTimer = null
    }

    this.$axios
      .get(`${ Urls.Consultation(consultationId) }get_v_nrs/`, params)
      .then(res => {
        this.vertragNumbers = res.data.vnrs || []
      })
      .catch(err => {
        this.$toast.error(err.message, 'Fehler')
        this.vertragNumbers = []
      })
  }

  onChangeValue (e) {
    if (!this.isFormValid('form-input')) {
      return
    }

    if (!this.$store.getters.hasConcept || this.isConcept) {
      this.fieldValue = e // value
      this.$emit('update', this.field.id, this.fieldValue)
      return
    }

    this.$confirmDialog.show({
      text: `Für die Beratung existiert bereits ein Konzept.\n` +
        `Wenn Sie die Änderung durchführen, müssen alle Änderungen an dem Konzept erneut durchgeführt werden.\n\n` +
        `Möchten Sie die Änderungen übernehmen?`,
      handleOk: () => {
        this.fieldValue = e // value
        this.$emit('update', this.field.id, this.fieldValue)
        this.$confirmDialog.close()
        this.deleteConcept()
      },
      handleCancel: () => {
        this.fieldValue = ''
        this.$nextTick(() => {
          this.fieldValue = this.field.value
        })
      }
    })
    // console.log('updated field value: ', e)
  }

  onShowCalcModal () {
    if (this.$refs.calculator) {
      this.$refs.calculator.onClear()
    }

    this.showCalcModal = true
    this.$nextTick(() => {
      this.calcValue = null
    })
  }

  onApplyCalcValue () {
    this.fieldValue = this.calcValue
    this.onChangeValue(this.calcValue)
    this.showCalcModal = false
  }

  onSearchValue (e) {
    // if (!this.$route.params || !this.$route.params.id) {
    //   return
    // }

    // if (this.searchTimer) {
    //   return
    // }

    // console.log('search: ', e)

    // this.searchTimer = setTimeout(() => {
    //   this.loadVertragNumbers()
    // }, 500)
  }
}
</script>

<style lang="sass">
.dynamic-field
  display: flex
  align-items: center
  width: 100%

.empty-highlight .v-input__slot
  background-color: #F9FBE7 !important
</style>
